/**
 * Import-HTML flow (Migration Tooling v0, TZ-04 §D): paste an HTML
 * email or pick a .html file, choose the source platform, import, land
 * in the editor. Parser warnings come back on the completed job and are
 * summarized inline before navigation so nothing is silently lost.
 */
import { useMutation } from "@tanstack/react-query";
import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../lib/api";
import {
  importHtml,
  type MigrationJob,
  type SourcePlatform,
} from "../lib/migrations";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const MAX_HTML_BYTES = 2_097_152; // mirrors the API/render-service cap

const PLATFORM_OPTIONS: Array<{ value: SourcePlatform; label: string }> = [
  { value: "generic", label: "Other / generic HTML" },
  { value: "sendpulse", label: "SendPulse" },
  { value: "stripo", label: "Stripo" },
  { value: "beefree", label: "Beefree" },
  { value: "mailchimp", label: "Mailchimp" },
];

export function ImportHtmlPanel({
  onImported,
  onCancel,
}: {
  onImported: (templateId: string, job: MigrationJob) => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [html, setHtml] = useState("");
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState<SourcePlatform>("generic");
  const [rehostImages, setRehostImages] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: () =>
      importHtml({ html, sourcePlatform: platform, name, rehostImages }),
    onSuccess: (job) => {
      if (job.template_id != null && job.template_id !== "") {
        onImported(job.template_id, job);
      } else {
        setError("Import finished without a template — try again.");
      }
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.problem.detail || err.problem.title
          : "Couldn't import this HTML.",
      );
    },
  });

  function onFilePicked(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file === undefined) return;
    setError(null);
    if (file.size > MAX_HTML_BYTES) {
      setError("This file is larger than the 2 MB import limit.");
      return;
    }
    void file.text().then((text) => {
      setHtml(text);
      setFileName(file.name);
      if (name.trim() === "") {
        setName(file.name.replace(/\.html?$/i, ""));
      }
    });
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (html.trim() === "") {
      setError("Paste the email HTML or choose a .html file first.");
      return;
    }
    if (new TextEncoder().encode(html).length > MAX_HTML_BYTES) {
      setError("This HTML is larger than the 2 MB import limit.");
      return;
    }
    mutation.mutate();
  }

  return (
    <section className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Import HTML</h2>
          <p className="text-sm text-neutral-600">
            Bring an existing email (SendPulse, Stripo, Mailchimp or any HTML) and
            keep editing it here. Complex fragments are preserved as HTML blocks.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </header>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-neutral-700">Source</span>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as SourcePlatform)}
              className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm"
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose .html file
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,text/html"
              onChange={onFilePicked}
              className="hidden"
              aria-label="HTML file"
            />
            {fileName !== null && (
              <span className="text-xs text-neutral-500">{fileName}</span>
            )}
          </div>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-neutral-700">Email HTML</span>
          <textarea
            value={html}
            onChange={(e) => {
              setHtml(e.target.value);
              setFileName(null);
            }}
            rows={8}
            placeholder="<!DOCTYPE html>…  paste the full email source here"
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 font-mono text-xs"
          />
        </label>

        <Input
          label="Template name"
          maxLength={255}
          value={name}
          onChange={(e) => setName(e.target.value)}
          hint="Optional — defaults to the email's <title>."
        />

        <label className="flex items-start gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={rehostImages}
            onChange={(e) => setRehostImages(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300"
          />
          <span>
            {t("import_html.rehost_images_label")}
            <span className="block text-xs text-neutral-500">
              {t("import_html.rehost_images_hint")}
            </span>
          </span>
        </label>

        {error !== null && (
          <p
            role="alert"
            className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" loading={mutation.isPending}>
          Import &amp; open editor
        </Button>
      </form>
    </section>
  );
}
